import axios from 'axios'
import firebase from 'firebase/compat/app'
import 'firebase/compat/auth'
import 'firebase/compat/firestore'
import 'firebase/compat/installations'
import 'firebase/compat/storage'
import { fetchAndActivate, getRemoteConfig, getValue } from 'firebase/remote-config'
import { useEffect } from 'react'

import firebaseWebConfig from '../firebase-web-config.json'
import { getParams } from '../hooks'
import {
  ActionType,
  BrandingColorType,
  BrandingThemeType,
  closeStripeError,
  ComponentAlignment,
  ComponentsDirection,
  ComponentType,
  Config,
  FastPreviewError,
  generateFirestoreId,
  getScreen,
  ISize,
  Module,
  ScreenSize,
  showConfirm,
  showError,
  showSuccess,
  validate,
  VariableSourceType,
} from '../utils'
import { F, FGF, FGP, FGPID, FGPV, FGV, FGVR, FirebaseContext, FNDA } from './index'
import type { SandboxChatUser } from '../utils/sandboxChatAuth'
import { SandboxChatAuth } from '../SandboxChatAuth'

let sandboxFirebaseConfigured = false
const externalScripts = new Map<string, Promise<void>>()

const previewRemoteConfigValues = (previewHost: string) => ({
  apiUrl: `${previewHost.replace(/\/+$/u, '')}/api`,
  oAuthClientId: 'PREVIEW_OAUTH_CLIENT_ID',
  stripePublishableKey: 'PREVIEW_STRIPE_PUBLISHABLE_KEY',
  vapidKey: 'PREVIEW_VAPID_KEY',
})

const getPreviewRemoteConfigValue = (previewHost: string, key: string) => {
  const values = previewRemoteConfigValues(previewHost)
  if (!Object.prototype.hasOwnProperty.call(values, key)) {
    throw new FastPreviewError(`Remote Config value "${key}" is unavailable.`)
  }
  return values[key as keyof typeof values]
}

const previewInstallationIdKey = 'codeplatform.fast-preview.installation-id'

const normalizePreviewProjectId = (projectId: string) => {
  const normalizedProjectId = projectId.toLowerCase()
  return normalizedProjectId.startsWith('demo-') ? normalizedProjectId : `demo-${normalizedProjectId}`
}

const createPreviewFirebaseConfig = (projectId: string): FirebaseConfig => {
  const normalizedProjectId = normalizePreviewProjectId(projectId)

  return {
    apiKey: `AIza${'A'.repeat(35)}`,
    authDomain: `${normalizedProjectId}.firebaseapp.com`,
    projectId: normalizedProjectId,
    storageBucket: `${normalizedProjectId}.firebasestorage.app`,
    messagingSenderId: '123456789012',
    appId: '1:123456789012:web:0123456789abcdef012345',
  }
}

const getPreviewInstallationId = () => {
  const existing = localStorage.getItem(previewInstallationIdKey)
  if (existing) {
    return Promise.resolve(existing)
  }

  const id = crypto.randomUUID()
  localStorage.setItem(previewInstallationIdKey, id)
  return Promise.resolve(id)
}

const configureSandboxFirebase = (
  auth: firebase.auth.Auth,
  firestore: firebase.firestore.Firestore,
  storage: firebase.storage.Storage,
  previewUrl: URL
) => {
  const sandboxAuth = auth as firebase.auth.Auth & {
    useEmulator(url: string, options: { disableWarnings: boolean }): void
  }
  sandboxAuth.useEmulator(previewUrl.origin, { disableWarnings: true })
  firestore.settings({ host: previewUrl.host, ssl: true })

  // The Storage emulator helper hard-codes HTTP, so configure its transport for ACA HTTPS directly.
  const sandboxStorage = storage as firebase.storage.Storage & {
    _delegate: { host: string; _isUsingEmulator: boolean; _protocol: string }
  }
  sandboxStorage._delegate.host = previewUrl.host
  sandboxStorage._delegate._isUsingEmulator = true
  sandboxStorage._delegate._protocol = 'https'
}

const loadExternalScript = (id: string, src: string) => {
  const pending = externalScripts.get(id)
  if (pending) {
    return pending
  }

  const loading = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(id) as HTMLScriptElement | null
    if (existing?.dataset.loaded === 'true') {
      resolve()
      return
    }
    const script = existing || document.createElement('script')
    script.id = id
    script.src = src
    script.async = true
    script.onload = () => {
      script.dataset.loaded = 'true'
      resolve()
    }
    script.onerror = () => reject(new Error(`Could not load ${id}.`))
    if (!existing) {
      document.body.appendChild(script)
    }
  })

  externalScripts.set(id, loading)
  return loading
}

export interface FirebaseConfig {
  [key: string]: string
}

interface Props {
  children: React.ReactNode
  webFirebaseConfig?: FirebaseConfig
  projectId?: string
  previewHost?: string
  sandboxUser?: SandboxChatUser
  version?: string
  firestorePrefix?: string
  inDevice: boolean
  language: string
  theme: BrandingThemeType
  setTheme: React.Dispatch<React.SetStateAction<BrandingThemeType>>
  desktopMode: boolean
  config: Config
}

export const FirebaseProvider: React.FC<Props> = ({
  children,
  webFirebaseConfig,
  projectId,
  previewHost,
  sandboxUser,
  version = '',
  firestorePrefix = '',
  inDevice,
  language,
  theme,
  setTheme,
  desktopMode,
  config,
}) => {
  const emailKey = 'email'
  const sandboxMode = Boolean(previewHost)
  const previewUrl = previewHost ? new URL(previewHost) : undefined
  const previewFirebaseConfig = projectId ? createPreviewFirebaseConfig(projectId) : undefined
  const firebaseConfig = sandboxMode
    ? previewFirebaseConfig
    : webFirebaseConfig || (firebaseWebConfig as FirebaseConfig)
  if (sandboxMode && !firebaseConfig) {
    throw new FastPreviewError('Sandbox Firebase configuration is missing.')
  }

  const app = firebase.apps.length ? firebase.app() : firebase.initializeApp(firebaseConfig!)

  const auth = firebase.auth()
  const firestore = firebase.firestore(app)
  const storage = firebase.storage(app)
  if (previewUrl && !sandboxFirebaseConfigured) {
    configureSandboxFirebase(auth, firestore, storage, previewUrl)
    sandboxFirebaseConfigured = true
  }

  const remoteConfig = sandboxMode ? undefined : getRemoteConfig(app)
  if (remoteConfig) {
    remoteConfig.settings.minimumFetchIntervalMillis = 0
  }

  const googleProvider = new firebase.auth.GoogleAuthProvider()
  googleProvider.setCustomParameters({ prompt: 'select_account' })

  const appleProvider = new firebase.auth.OAuthProvider('apple.com')

  const signInWithGoogle = async () => {
    await auth.signInWithPopup(googleProvider)
  }

  const signInWithApple = async () => {
    await auth.signInWithPopup(appleProvider)
  }

  const createUserWithEmailAndPassword: FGP = async (...props) => {
    const { email, password } = await getParams(...props)
    await auth.createUserWithEmailAndPassword(email || '', password || '')
  }

  const signInWithEmailAndPassword: FGP = async (...props) => {
    const { email, password } = await getParams(...props)
    await auth.signInWithEmailAndPassword(email || '', password || '')
  }

  const sendPasswordResetEmail: FGV = async (getVariableValue, variable) => {
    if (sandboxMode) {
      throw new FastPreviewError('Password reset email is unavailable.')
    }
    const email = (await getVariableValue(variable)) || ''
    await auth.sendPasswordResetEmail(email).then(() => showSuccess(`Link was sent to ${email}`))
  }

  const sendSignInLinkToEmail: FGVR = async (getVariableValue, variable, resend) => {
    if (sandboxMode) {
      throw new FastPreviewError(`${resend ? 'Email-link resend' : 'Email-link sending'} is unavailable.`)
    }
    const email = (await getVariableValue(variable)) || ''
    await auth.sendSignInLinkToEmail(email, { url: window.location.origin, handleCodeInApp: true }).then(() => {
      localStorage.setItem(emailKey, email)
      if (resend) {
        showSuccess(`Link was sent to ${email}`)
      }
    })
  }

  const reSendSignInLinkToEmail: FGV = async (...props) => {
    if (sandboxMode) {
      throw new FastPreviewError('Email-link resend is unavailable.')
    }
    if (await showConfirm('Trouble getting email? Resend?')) {
      await sendSignInLinkToEmail(...props, true)
    }
  }

  const httpClient = axios.create({ headers: { 'Content-Type': 'application/json' } })

  httpClient.interceptors.request.use(
    async config => {
      const accessToken = await auth.currentUser?.getIdToken()
      if (accessToken) {
        config.headers['Authorization'] = `Bearer ${accessToken}`
      }
      return config
    },
    err => Promise.reject(err)
  )

  httpClient.interceptors.response.use(
    res => res,
    async err => {
      if (err.response) {
        const originalConfig = err.config
        if (err.response.status === 401 && !originalConfig._retry) {
          originalConfig._retry = true
          const accessToken = await auth.currentUser?.getIdToken(true)
          if (accessToken) {
            httpClient.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
          }
          return httpClient(originalConfig)
        }
      }
      return Promise.reject(err)
    }
  )

  const getApiUrl = () => {
    const errorMessage = 'Backend is not deployed!'
    const { apiUrl } = config.data.remoteConfig || {}
    if (!apiUrl) {
      throw new Error(errorMessage)
    }
    const value = getRemoteConfigValue(apiUrl)
    if (!value) {
      throw new Error(errorMessage)
    }
    return value
  }

  const getRemoteConfigValue = (key: string) => {
    if (sandboxMode) {
      return getPreviewRemoteConfigValue(previewHost!, key)
    }
    return getValue(remoteConfig!, key).asString()
  }

  const activateRemoteConfig = async () => {
    if (remoteConfig) {
      await fetchAndActivate(remoteConfig)
    }
  }

  const getInstallationId = () => (sandboxMode ? getPreviewInstallationId() : app.installations().getId())

  const getNotificationToken = async () => {
    if (sandboxMode) {
      throw new FastPreviewError('Notifications are unavailable.')
    }
    const errorMessage = 'No vapidKey!'
    const { vapidKey } = config.data.remoteConfig || {}
    if (!vapidKey) {
      throw new Error(errorMessage)
    }
    const value = getRemoteConfigValue(vapidKey)
    if (!value) {
      throw new Error(errorMessage)
    }
    const { getMessaging, getToken, onMessage } = await import('firebase/messaging')
    const messaging = getMessaging(app)
    onMessage(messaging, payload => {
      const { title = '', body, icon } = payload.notification || {}
      new Notification(title, { body, icon })
    })
    return getToken(messaging, { vapidKey: value })
  }

  const setNotificationToken = async (canScipToken?: boolean) => {
    const installationId = await getInstallationId()
    let fcmToken: string | undefined = undefined
    try {
      fcmToken = await getNotificationToken()
    } catch (err: any) {
      if (!canScipToken) {
        throw err
      }
    }
    const { onDeviceRegister } = config.authentication.triggers
    await runFlow(onDeviceRegister, {
      installationId,
      fcmToken,
      platform: 'web',
      name: window.navigator.userAgent,
    })
  }

  const deleteNotificationToken = async () => {
    const installationId = await getInstallationId()
    const { onDeviceUnregister } = config.authentication.triggers
    await runFlow(onDeviceUnregister, { installationId })
  }

  const checkNotificationsEnabled = async () => {
    if (sandboxMode) {
      return false
    }
    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        await setNotificationToken()
        return true
      } catch {
        return false
      }
    } else {
      return false
    }
  }

  const enableNotifications = async () => {
    if (sandboxMode) {
      throw new FastPreviewError('Notifications are unavailable.')
    }
    if ('Notification' in window) {
      const permission = await Notification.requestPermission()
      if (permission === 'granted') {
        await setNotificationToken()
        return
      }
    }
    throw new Error('User Notifications Permission Denied. Enable Notifications in browser settings.')
  }

  const logout = async () => {
    try {
      await deleteNotificationToken()
    } catch {}
    await auth.signOut()
  }

  const runFlow: FNDA = async (name, data, asynchronous) => {
    const flow = config.logic.flows?.find(el => el.name === name)
    if (!flow) {
      throw new Error(`No flow ${name} finded!`)
    }
    if (data) {
      const errorMessage = validate(data, flow.inputProperties || [], config.data.dataObjects || [])
      if (errorMessage) {
        throw new Error(errorMessage)
      }
    }
    const apiUrl = getApiUrl()
    return httpClient.post(`${apiUrl}/v1/flows/${name}${asynchronous ? '?async=true' : ''}`, data)
  }

  const search = (url: string) => {
    const apiUrl = getApiUrl()
    return httpClient.get(`${apiUrl}/v1/${url}`)
  }

  const oauth: FGPV = (getVariableValue, parameters, valueTarget) =>
    new Promise(async (resolve, reject) => {
      if (sandboxMode) {
        reject(new FastPreviewError('OAuth is unavailable.'))
        return
      }
      const { provider, scopes } = await getParams(getVariableValue, parameters)
      if (provider === 'google' && Array.isArray(scopes)) {
        const errorMessage = 'No oAuthClientId!'
        const { oAuthClientId } = config.data.remoteConfig || {}
        if (!oAuthClientId) {
          throw new Error(errorMessage)
        }
        const client_id = getRemoteConfigValue(oAuthClientId)
        if (!client_id) {
          throw new Error(errorMessage)
        }
        await loadExternalScript('googleIdentity', 'https://accounts.google.com/gsi/client')
        const codeClient = google.accounts.oauth2.initCodeClient({
          client_id,
          scope: scopes.join(' '),
          ux_mode: 'popup',
          callback: async ({ code }) => {
            const value = JSON.stringify({ client_id, redirect_uri: 'postmessage', scopes, code })
            await getVariableValue({ source: valueTarget }, undefined, { value })
            resolve()
          },
          error_callback: err => reject(err),
        })
        codeClient.requestCode()
      } else {
        reject(new Error('Something went wrong!'))
      }
    })

  const stripeContainerId = 'stripe-container'
  const stripeCloseId = 'stripe-close'
  const checkoutSelected: FGPID = (getVariable, priceId) =>
    new Promise(async (resolve, reject) => {
      if (sandboxMode) {
        reject(new FastPreviewError('Purchases are unavailable.'))
        return
      }
      const { onStripeCheckout, onStripeCheckoutEnded } = config.billing?.triggers || {}
      if (onStripeCheckout && onStripeCheckoutEnded) {
        try {
          const errorMessage = 'No stripePublishableKey!'
          const { stripePublishableKey } = config.data.remoteConfig || {}
          if (!stripePublishableKey) {
            throw new Error(errorMessage)
          }
          const stripe_key = getRemoteConfigValue(stripePublishableKey)
          if (!stripe_key) {
            throw new Error(errorMessage)
          }
          const {
            data: { id: checkoutSessionId, clientSecret },
          } = await runFlow(onStripeCheckout, { priceId })
          await loadExternalScript('stripe', 'https://js.stripe.com/basil/stripe.js')
          // @ts-ignore
          const stripe = Stripe(stripe_key)
          const checkout = await stripe.initEmbeddedCheckout({
            clientSecret,
            onComplete: () =>
              runFlow(onStripeCheckoutEnded, { checkoutSessionId })
                .then(() => {
                  hideStripe()
                  checkout.destroy()
                  resolve(null)
                })
                .catch(err => {
                  hideStripe()
                  checkout.destroy()
                  reject(err)
                }),
          })
          const root = document.getElementById('root') as HTMLElement
          const screenParentElement = document.createElement('div')
          screenParentElement.className = 'dialog'
          root.appendChild(screenParentElement)
          const hideStripe = () => screenParentElement.parentNode?.removeChild(screenParentElement)
          await getScreen(
            f,
            {
              backgroundColor: { constant: `@${BrandingColorType.white}` },
              components: [
                {
                  componentAlignment: ComponentAlignment.fill,
                  componentType: ComponentType.scroll,
                  componentsDirection: ComponentsDirection.vertical,
                  contentPadding: { constant: { bottom: ISize.bottomScreenMargins, top: ISize.topScreenMargins } },
                  id: stripeContainerId,
                  customId: stripeContainerId,
                  name: stripeContainerId,
                },
              ],
              dialog: true,
              enabled: { constant: true },
              id: 'stripe',
              module: Module.paywall,
              path: 'Paywall/Paywall',
              screenName: 'Stripe',
              showTopBar: { constant: true },
              size: ScreenSize.compact,
              topBar: {
                trailingComponents: [
                  {
                    actions: [{ actionType: ActionType.placeholder }],
                    componentAlignment: ComponentAlignment.fill,
                    componentType: ComponentType.button,
                    height: { constant: '32' },
                    image: { constant: { resourceId: 'kyi2fkbmdekrgxvrqenv' } },
                    imageColor: { constant: `@${BrandingColorType.black}` },
                    width: { constant: '32' },
                    id: stripeCloseId,
                    customId: stripeCloseId,
                    name: stripeCloseId,
                  },
                ],
              },
            },
            config,
            screenParentElement,
            language,
            theme,
            setTheme,
            desktopMode,
            false,
            () => {},
            () => {},
            () => {},
            () => {},
            async () => {},
            () => {},
            getVariable,
            async () => {},
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            true
          )
          const stripeContainer = document.getElementById(stripeContainerId) as HTMLElement
          stripeContainer.style.removeProperty('flex-direction')
          checkout.mount(`#${stripeContainerId}`)
          const closeButton = document.getElementById(stripeCloseId) as HTMLElement
          closeButton.onclick = () => {
            hideStripe()
            checkout.destroy()
            reject(new Error(closeStripeError))
          }
        } catch (err: any) {
          reject(err)
        }
      } else {
        reject(new Error('No onStripeCheckout or onStripeCheckoutEnded!'))
      }
    })

  useEffect(() => {
    const closeButton = document.getElementById(stripeCloseId)
    closeButton?.click()
  }, [inDevice, language, theme, desktopMode])

  const restorePurchases = () =>
    new Promise(async (resolve, reject) => {
      if (sandboxMode) {
        reject(new FastPreviewError('Purchases are unavailable.'))
        return
      }
      const { onStripeRestorePurchases } = config.billing?.triggers || {}
      if (onStripeRestorePurchases) {
        try {
          const res = await runFlow(onStripeRestorePurchases)
          showSuccess('Your purchases have been successfully restored.')
          resolve(res)
        } catch (err: any) {
          reject(err)
        }
      } else {
        reject(new Error('No onStripeRestorePurchases!'))
      }
    })

  const getAssetRecord: FGF = (getVariableValue, file) =>
    new Promise(async resolve => {
      const currentUserId = await getVariableValue({
        source: { type: VariableSourceType.globalVariable, variableName: 'currentUserId' },
      })
      const fileRef = storage.ref(`uploads/${currentUserId}/${generateFirestoreId()}/${file.name}`)
      fileRef
        .put(file)
        .then(() =>
          fileRef.getDownloadURL().then(async url => {
            const { onMetadataExtract } = config.assets.triggers
            const {
              data: { bytes, resourceType, mimeType, format, width, height, duration, pageCount },
            } = await runFlow(onMetadataExtract, { url })
            resolve({
              bytes,
              duration,
              error: null,
              folder: '~',
              format,
              height,
              originalFileUri: fileRef.toString(),
              pageCount,
              resourceType,
              signature: null,
              status: null,
              thumbnailUrl: null,
              title: file.name,
              url,
              version: null,
              width,
              mimeType,
              startTime: null,
              ownerId: currentUserId,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            })
          })
        )
        .catch(err => showError(err))
        .finally(() => resolve(null))
    })

  const getStripePriceDetails = (priceId: string) =>
    new Promise(async (resolve, reject) => {
      if (sandboxMode) {
        reject(new FastPreviewError('Purchases are unavailable.'))
        return
      }
      const { onStripePriceDetails } = config.billing?.triggers || {}
      if (onStripePriceDetails) {
        try {
          const res = await runFlow(onStripePriceDetails, { priceId })
          resolve(res)
        } catch (err: any) {
          reject(err)
        }
      } else {
        reject(new Error('No onStripePriceDetails!'))
      }
    })

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      if (user && !sandboxMode) {
        setNotificationToken(true).catch(() => {})
      }
    })
    return () => {
      unsubscribe()
    }
  }, [])

  const loadGoogleMaps = () => {
    if (sandboxMode) {
      return Promise.reject(new FastPreviewError('Google Maps is unavailable.'))
    }
    return loadExternalScript(
      'googleMaps',
      `https://maps.googleapis.com/maps/api/js?key=${firebaseConfig!.apiKey}&loading=async&callback=Function.prototype`
    )
  }

  const f: F = {
    version,
    firestorePrefix,
    auth,
    firestore,
    activateRemoteConfig,
    getRemoteConfigValue,
    mapsEnabled: !sandboxMode,
    loadGoogleMaps,
    signInWithGoogle,
    signInWithApple,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    sendPasswordResetEmail,
    sendSignInLinkToEmail,
    reSendSignInLinkToEmail,
    checkNotificationsEnabled,
    enableNotifications,
    logout,
    runFlow,
    search,
    oauth,
    checkoutSelected,
    restorePurchases,
    getAssetRecord,
    getStripePriceDetails,
    emailKey,
  }

  return (
    <FirebaseContext.Provider value={{ f }}>
      {sandboxMode && sandboxUser ? (
        <SandboxChatAuth key={JSON.stringify(sandboxUser)} auth={auth} user={sandboxUser}>
          {children}
        </SandboxChatAuth>
      ) : (
        children
      )}
    </FirebaseContext.Provider>
  )
}
